import { DateTime } from "luxon";
import { Collab, CollabStatus } from "../../gql/__generated__/graphql";

const EXPIRATION_DAYS_DEFAULT = 14; // 2 weeks
const EXPIRATION_WARNING_DAYS = 3; // Show warning when <3 days left

/**
 * Get the number of days until expiration for a given collab status
 * @param status - The collab status
 * @returns Number of days until expiration, or null if the status doesn't expire
 */
export function getExpirationDays(status: CollabStatus): number | null {
  const nonExpiringStatuses: CollabStatus[] = [
    "AWAITING_TRANSFER",
    "COMPLETED",
    "DECLINED",
    "EXPIRED",
  ];

  return nonExpiringStatuses.includes(status) ? null : EXPIRATION_DAYS_DEFAULT;
}

/**
 * Calculate the expiration date for a collab
 * @param collab - The collab object
 * @returns DateTime of expiration, or null if the collab doesn't expire
 */
export function calculateCollabExpiration(collab: Collab): DateTime | null {
  // Use backend expiresAt if available, otherwise calculate from updatedAt
  if (collab.expiresAt) {
    return DateTime.fromISO(collab.expiresAt);
  }

  const days = getExpirationDays(collab.status);
  if (days === null) return null;

  return DateTime.fromISO(collab.updatedAt).plus({ days });
}

/**
 * Get a formatted display string for the expiration date
 * @param collab - The collab object
 * @returns Formatted expiration string, or null if doesn't expire
 */
export function getCollabExpirationDisplay(collab: Collab): string | null {
  const expiration = calculateCollabExpiration(collab);
  if (!expiration) return null;

  const now = DateTime.utc();
  const isExpired = expiration < now;

  if (isExpired) {
    return `Expired on ${expiration.toLocaleString(DateTime.DATE_MED)}`;
  }

  return `Expires ${expiration.toLocaleString(DateTime.DATE_MED)}`;
}

/**
 * Check if a collab is expiring soon (within warning threshold)
 * @param collab - The collab object
 * @returns True if expiring within the warning threshold
 */
export function isCollabExpiringSoon(collab: Collab): boolean {
  const expiration = calculateCollabExpiration(collab);
  if (!expiration) return false;

  const now = DateTime.utc();
  const daysUntilExpiration = expiration.diff(now, "days").days;

  return daysUntilExpiration > 0 && daysUntilExpiration <= EXPIRATION_WARNING_DAYS;
}

/**
 * Check if a collab has already expired
 * @param collab - The collab object
 * @returns True if the collab has expired
 */
export function isCollabExpired(collab: Collab): boolean {
  const expiration = calculateCollabExpiration(collab);
  if (!expiration) return false;

  return expiration < DateTime.utc();
}
